import { moveDecimalPoint } from '@caudena/core/utils/math'
import type { UnsupportedCoinType } from '@caudena/web-platform/dist/types'
import { isObject } from '@caudena/core/utils/isObject'
import { IconVariant } from '@caudena/web-platform'

// ========== DEFAULT CONFIGURATION ==========

export const DEFAULT_CRYPTO_CONFIG = {
  currencies: [
    {
      coin: 'BITCOIN',
      code: 'BTC',
      name: 'Bitcoin',
      exponent: 8,
      type: 'UTXO',
      index: 0,
    },
    {
      coin: 'ETHEREUM',
      code: 'ETH',
      name: 'Ethereum',
      exponent: 18,
      type: 'EVM',
      index: 1,
    },
    {
      coin: 'LITECOIN',
      code: 'LTC',
      name: 'Litecoin',
      exponent: 8,
      type: 'UTXO',
      index: 2,
    },
    {
      coin: 'DOGECOIN',
      code: 'DOGE',
      name: 'Dogecoin',
      exponent: 8,
      type: 'UTXO',
      index: 3,
    },
    {
      coin: 'BINANCE',
      code: 'BNB',
      name: 'Binance',
      exponent: 18,
      type: 'EVM',
      index: 4,
    },
    {
      coin: 'TRON',
      code: 'TRX',
      name: 'Tron',
      exponent: 18,
      type: 'EVM',
      index: 5,
    },
    {
      coin: 'BASE',
      code: 'BASE',
      name: 'Base',
      exponent: 18,
      type: 'EVM',
      index: 6,
    },
  ],
  symbols: {
    USD: '$',
    EUR: '€',
  },
} as const

export const DEFAULT_TOKEN_ICON_BY_CURRENCY: Record<
  CryptoTypesResult['CoinType'],
  IconVariant
> = {
  btc: 'Btc',
  eth: 'Eth',
  bnb: 'Bnb',
  trx: 'Trx',
  doge: 'Doge',
  ltc: 'Ltc',
  base: 'EthBase',
}

// ========== CRYPTO CONFIGURATION TYPES ==========

export interface CryptoConfig {
  coin: string // BITCOIN, ETHEREUM, etc.
  code: string // BTC, ETH, etc.
  name: string // Bitcoin, Ethereum, etc.
  exponent: number // 8, 18, etc. (decimal places for smallest unit)
  type: 'UTXO' | 'EVM' // blockchain type
  index?: number // optional index for mapping
}

export interface CryptoConfiguration {
  currencies: readonly CryptoConfig[]
  symbols?: Record<string, string>
}

// ========== CRYPTO CONFIGURATION FACTORY ==========

export const createCryptoConfiguration = <T extends CryptoConfiguration>(
  config: T
) => {
  // Generate COIN constants - preserve literal types
  const COIN = config.currencies.reduce(
    (acc, curr) => {
      acc[curr.coin as string] = curr.coin
      return acc
    },
    {} as { [K in T['currencies'][number]['coin']]: K }
  )

  // Generate COIN_CODES constants - preserve literal types
  const COIN_CODES = config.currencies.reduce(
    (acc, curr) => {
      acc[curr.code as string] = curr.code
      return acc
    },
    {} as { [K in T['currencies'][number]['code']]: K }
  )

  // Generate CODE_BY_CURRENCY mapping
  const CODE_BY_CURRENCY = config.currencies.reduce(
    (acc, curr) => {
      acc[curr.coin as string] = curr.code
      return acc
    },
    {} as {
      [K in T['currencies'][number]['coin']]: Extract<
        T['currencies'][number],
        { coin: K }
      >['code']
    }
  )

  // Generate CURRENCY_BY_CODE mapping
  const CURRENCY_BY_CODE = config.currencies.reduce(
    (acc, curr) => {
      acc[curr.code as string] = curr.coin
      return acc
    },
    {} as {
      [K in T['currencies'][number]['code']]: Extract<
        T['currencies'][number],
        { code: K }
      >['coin']
    }
  )

  // Generate COIN_NAMES mapping
  const COIN_NAMES = config.currencies.reduce(
    (acc, curr) => {
      acc[curr.code as string] = curr.name
      return acc
    },
    {} as {
      [K in T['currencies'][number]['code']]: Extract<
        T['currencies'][number],
        { code: K }
      >['name']
    }
  )

  // Generate CURRENCY_SYMBOLS with proper union types
  const CURRENCY_SYMBOLS = config.symbols as T['symbols']

  // Generate CURRENCY_UNIT_EXPONENT mapping
  const CURRENCY_UNIT_EXPONENT = config.currencies.reduce(
    (acc, curr) => {
      acc[curr.coin as string] = curr.exponent
      return acc
    },
    {} as {
      [K in T['currencies'][number]['coin']]: Extract<
        T['currencies'][number],
        { coin: K }
      >['exponent']
    }
  )

  // Generate COIN_CODE_BY_INDEX mapping
  const COIN_CODE_BY_INDEX = config.currencies.reduce(
    (acc, curr, index) => {
      const mappingIndex = curr.index !== undefined ? curr.index : index
      acc[mappingIndex] = curr.code
      return acc
    },
    {} as Record<number, T['currencies'][number]['code']>
  )

  // Generate lowercase coin names record
  type LowercaseKeys<U> = { [K in keyof U as Lowercase<K & string>]: U[K] }
  const COIN_NAMES_RECORD: LowercaseKeys<typeof COIN_NAMES> = Object.entries(
    COIN_NAMES
  ).reduce(
    (acc, [key, value]: [string, string]) => {
      acc[key.toLowerCase()] = value
      return acc
    },
    {} as LowercaseKeys<typeof COIN_NAMES>
  )

  // Generate arrays with proper typing
  const COINNAMES = Object.values(COIN) as T['currencies'][number]['coin'][]
  const BLOCKCHAINS = config.currencies.map(
    (curr) => curr.code
  ) as T['currencies'][number]['code'][]
  const UTXO_BLOCKCHAINS = config.currencies
    .filter((curr) => curr.type === 'UTXO')
    .map((curr) => curr.code) as Extract<
    T['currencies'][number],
    { type: 'UTXO' }
  >['code'][]
  const EVM_BLOCKCHAINS = config.currencies
    .filter((curr) => curr.type === 'EVM')
    .map((curr) => curr.code) as Extract<
    T['currencies'][number],
    { type: 'EVM' }
  >['code'][]
  const COINS = config.currencies.map((curr) =>
    curr.code.toLowerCase()
  ) as Lowercase<T['currencies'][number]['code']>[]
  const UTXO_COINS = config.currencies
    .filter((curr) => curr.type === 'UTXO')
    .map((curr) => curr.code.toLowerCase()) as Lowercase<
    Extract<T['currencies'][number], { type: 'UTXO' }>['code']
  >[]
  const EVM_COINS = config.currencies
    .filter((curr) => curr.type === 'EVM')
    .map((curr) => curr.code.toLowerCase()) as Lowercase<
    Extract<T['currencies'][number], { type: 'EVM' }>['code']
  >[]

  // ========== GENERATED TYPES ==========
  type CoinData = typeof COIN
  type Coin = keyof typeof COIN
  type CurrencySymbolsType = keyof typeof CURRENCY_SYMBOLS
  type CodeByCurrency = typeof CODE_BY_CURRENCY
  type CurrencyByCode = typeof CURRENCY_BY_CODE
  type CurrencySymbols = typeof CURRENCY_SYMBOLS
  type CoinNames = typeof COIN_NAMES
  type CurrencyUnitExponent = typeof CURRENCY_UNIT_EXPONENT
  type CoinCodeByIndex = typeof COIN_CODE_BY_INDEX

  type CoinCodes = typeof COIN_CODES
  type CoinCodesType = keyof typeof COIN_CODES
  type CoinType = Lowercase<CoinCodesType>
  type BlockchainType = Uppercase<CoinType>

  type CoinTypeUTXO = (typeof UTXO_COINS)[number]
  type BlockchainTypeUTXO = (typeof UTXO_BLOCKCHAINS)[number]

  type CoinTypeEVM = (typeof EVM_COINS)[number]
  type BlockchainTypeEVM = (typeof EVM_BLOCKCHAINS)[number]

  type AllCoinType = CoinType | UnsupportedCoinType
  type SupportedCoin<T extends AllCoinType> = Extract<T, CoinType>
  type UnsupportedCoin<T extends AllCoinType> = Exclude<T, CoinType>

  // ========== UTILITY FUNCTIONS ==========

  const getCoinCode = (currencyKey: Coin) =>
    CODE_BY_CURRENCY[currencyKey.toUpperCase()]

  const getCoinKey = (coin: CoinCodesType) =>
    CURRENCY_BY_CODE[coin.toUpperCase()].toLowerCase()

  const getCoinName = (coin: Lowercase<CoinCodesType> | CoinCodesType) =>
    COIN_NAMES[coin.toUpperCase()]

  const getCurrencyByIndex = (index: number): CoinType =>
    COIN_CODE_BY_INDEX[index]?.toLowerCase() as CoinType

  const getCurrency = (currency: number | CoinType): CoinType =>
    typeof currency === 'number' ? getCurrencyByIndex(currency) : currency

  const getCoinExponent = (coin: CoinCodesType) =>
    CURRENCY_UNIT_EXPONENT[CURRENCY_BY_CODE[coin.toUpperCase()]]

  const isCoin = (currency: CoinCodesType) =>
    Object.values(COIN_CODES).includes(currency.toUpperCase() as CoinCodesType)

  const getCurrencySymbol = (currency: CurrencySymbolsType) =>
    CURRENCY_SYMBOLS?.[(currency as string).toUpperCase()] || ''

  const getRealCoinValue = (coin, value) =>
    coin && isCoin(coin)
      ? moveDecimalPoint(value, getCoinExponent(coin))
      : value

  // ========== RETURN CONFIGURATION OBJECT ==========
  return {
    // Constants
    COIN,
    COIN_CODES,
    CODE_BY_CURRENCY,
    CURRENCY_BY_CODE,
    COIN_NAMES,
    CURRENCY_SYMBOLS,
    CURRENCY_UNIT_EXPONENT,
    COIN_CODE_BY_INDEX,
    COIN_NAMES_RECORD,
    COINNAMES,
    BLOCKCHAINS,
    UTXO_BLOCKCHAINS,
    EVM_BLOCKCHAINS,
    COINS,
    UTXO_COINS,
    EVM_COINS,

    // Functions
    getCoinCode,
    getCoinKey,
    getCoinName,
    getCurrencyByIndex,
    getCurrency,
    getCoinExponent,
    isCoin,
    getCurrencySymbol,
    getRealCoinValue,

    // Types (for TS usage)
    types: {} as {
      CoinData: CoinData
      Coin: Coin
      CurrencySymbolsType: CurrencySymbolsType
      CodeByCurrency: CodeByCurrency
      CurrencyByCode: CurrencyByCode
      CurrencySymbols: CurrencySymbols
      CoinNames: CoinNames
      CurrencyUnitExponent: CurrencyUnitExponent
      CoinCodeByIndex: CoinCodeByIndex
      CoinCodes: CoinCodes
      CoinCodesType: CoinCodesType
      CoinType: CoinType
      BlockchainType: BlockchainType
      CoinTypeUTXO: CoinTypeUTXO
      BlockchainTypeUTXO: BlockchainTypeUTXO
      CoinTypeEVM: CoinTypeEVM
      BlockchainTypeEVM: BlockchainTypeEVM
      AllCoinType: AllCoinType
      SupportedCoin: SupportedCoin<AllCoinType>
      UnsupportedCoin: UnsupportedCoin<AllCoinType>
    },
  }
}

// ========== DEFAULT EXPORT (BACKWARD COMPATIBILITY) ==========

const defaultConfig = createCryptoConfiguration(DEFAULT_CRYPTO_CONFIG)

// Export all constants and functions for backward compatibility
export const {
  COIN,
  COIN_CODES,
  CODE_BY_CURRENCY,
  CURRENCY_BY_CODE,
  COIN_NAMES,
  CURRENCY_SYMBOLS,
  CURRENCY_UNIT_EXPONENT,
  COIN_CODE_BY_INDEX,
  COIN_NAMES_RECORD,
  COINNAMES,
  BLOCKCHAINS,
  UTXO_BLOCKCHAINS,
  EVM_BLOCKCHAINS,
  COINS,
  UTXO_COINS,
  EVM_COINS,
  getCoinCode,
  getCoinKey,
  getCoinName,
  getCurrencyByIndex,
  getCurrency,
  getCoinExponent,
  isCoin,
  getCurrencySymbol,
  getRealCoinValue,
} = defaultConfig

// ========== CRYPTO TYPE GUARDS GENERATOR ==========

// Define the interface for crypto configuration result
interface CryptoTypeGuardsConstraint {
  UTXO_COINS: readonly string[]
  EVM_COINS: readonly string[]
  COINS: readonly string[]
  EVM_BLOCKCHAINS: readonly string[]
  UTXO_BLOCKCHAINS: readonly string[]
  BLOCKCHAINS: readonly string[]
}

export const createCryptoTypeGuards = <T extends CryptoTypeGuardsConstraint>(
  crypto: T
) => {
  // Use actual arrays from the crypto configuration
  const UTXO_COINS = crypto.UTXO_COINS
  const EVM_COINS = crypto.EVM_COINS
  const COINS = crypto.COINS
  const EVM_BLOCKCHAINS = crypto.EVM_BLOCKCHAINS
  const UTXO_BLOCKCHAINS = crypto.UTXO_BLOCKCHAINS

  // Type aliases using the actual types from the generic parameter T
  type CoinType = T['COINS'][number]
  type AllCoinType = CoinType | UnsupportedCoinType
  type BlockchainType = T['BLOCKCHAINS'][number]
  type CoinTypeUTXO = T['UTXO_COINS'][number]
  type CoinTypeEVM = T['EVM_COINS'][number]
  type BlockchainTypeUTXO = T['UTXO_BLOCKCHAINS'][number]
  type BlockchainTypeEVM = T['EVM_BLOCKCHAINS'][number]
  type SupportedCoin<TCoin extends AllCoinType> = Extract<TCoin, CoinType>
  type UnsupportedCoin<TCoin extends AllCoinType> = Exclude<TCoin, CoinType>

  type ArgIsCurrency = { currency: CoinType } | CoinType
  type ArgIsAllCurrency = { currency: AllCoinType } | AllCoinType
  type ArgIsBlockchain = { blockchain: BlockchainType } | BlockchainType

  // UTXO type guards
  function isUTXO<TCoin extends CoinType>(
    coin: TCoin
  ): coin is TCoin & CoinTypeUTXO

  function isUTXO<TArg extends { currency: CoinTypeEVM | CoinTypeUTXO }>(
    arg: TArg
  ): arg is Extract<TArg, { currency: CoinTypeUTXO }>

  function isUTXO<TArg extends ArgIsCurrency>(
    arg: TArg
  ): arg is Extract<TArg, { currency: CoinTypeUTXO } | CoinTypeUTXO> {
    const objectCurrency = arg as { currency: CoinType }

    if (isObject(objectCurrency)) {
      return UTXO_COINS.includes(objectCurrency.currency as CoinTypeUTXO)
    }

    return UTXO_COINS.includes(arg as CoinTypeUTXO)
  }

  // EVM type guards
  function isEVM<TCoin extends CoinType>(
    coin: TCoin
  ): coin is TCoin & CoinTypeEVM

  function isEVM<TArg extends { currency: CoinType }>(
    arg: TArg
  ): arg is Extract<TArg, { currency: CoinTypeEVM }>

  function isEVM<TArg extends ArgIsCurrency>(
    arg: TArg
  ): arg is Extract<TArg, { currency: CoinTypeEVM } | CoinTypeEVM> {
    const objectCurrency = arg as { currency: CoinType }

    if (isObject(objectCurrency)) {
      return EVM_COINS.includes(objectCurrency.currency as CoinTypeEVM)
    }

    return EVM_COINS.includes(arg as CoinTypeEVM)
  }

  // Supported blockchain type guards
  function isSupportedBlockchain<TCoin extends AllCoinType>(
    coin: TCoin
  ): coin is SupportedCoin<TCoin>

  function isSupportedBlockchain<TArg extends { currency: AllCoinType }>(
    arg: TArg
  ): arg is Extract<TArg, { currency: SupportedCoin<AllCoinType> }>

  function isSupportedBlockchain<TArg extends ArgIsAllCurrency>(
    arg: TArg
  ): arg is Extract<
    TArg,
    { currency: SupportedCoin<AllCoinType> } | SupportedCoin<AllCoinType>
  > {
    const objectCurrency = arg as { currency: AllCoinType }

    if (isObject(objectCurrency)) {
      return COINS.includes(objectCurrency.currency as CoinType)
    }

    return COINS.includes(arg as CoinType)
  }

  // Unsupported blockchain type guards
  function isUnsupportedBlockchain<TCoin extends AllCoinType>(
    coin: TCoin
  ): coin is UnsupportedCoin<TCoin>

  function isUnsupportedBlockchain<TArg extends { currency: AllCoinType }>(
    arg: TArg
  ): arg is Extract<TArg, { currency: UnsupportedCoin<AllCoinType> }>

  function isUnsupportedBlockchain<TArg extends ArgIsAllCurrency>(
    arg: TArg
  ): arg is Extract<
    TArg,
    { currency: UnsupportedCoin<AllCoinType> } | UnsupportedCoin<AllCoinType>
  > {
    const objectCurrency = arg as { currency: AllCoinType }

    if (isObject(objectCurrency)) {
      return !COINS.includes(objectCurrency.currency as CoinType)
    }

    return !COINS.includes(arg as CoinType)
  }

  // Blockchain EVM type guards
  function isBlockchainEVM<TBlockchain extends BlockchainType>(
    blockchain: TBlockchain
  ): blockchain is TBlockchain & BlockchainTypeEVM

  function isBlockchainEVM<TArg extends { blockchain: BlockchainType }>(
    arg: TArg
  ): arg is Extract<TArg, { blockchain: BlockchainTypeEVM }>

  function isBlockchainEVM<TArg extends ArgIsBlockchain>(
    arg: TArg
  ): arg is Extract<
    TArg,
    { blockchain: BlockchainTypeEVM } | BlockchainTypeEVM
  > {
    const objectCurrency = arg as { blockchain: BlockchainType }

    if (isObject(objectCurrency)) {
      return EVM_BLOCKCHAINS.includes(
        objectCurrency.blockchain as BlockchainTypeEVM
      )
    }

    return EVM_BLOCKCHAINS.includes(arg as BlockchainTypeEVM)
  }

  // Blockchain UTXO type guards
  function isBlockchainUTXO<TBlockchain extends BlockchainType>(
    blockchain: TBlockchain
  ): blockchain is TBlockchain & BlockchainTypeUTXO

  function isBlockchainUTXO<TArg extends { blockchain: BlockchainType }>(
    arg: TArg
  ): arg is Extract<TArg, { blockchain: BlockchainTypeUTXO }>

  function isBlockchainUTXO<TArg extends ArgIsBlockchain>(
    arg: TArg
  ): arg is Extract<
    TArg,
    { blockchain: BlockchainTypeUTXO } | BlockchainTypeUTXO
  > {
    const objectCurrency = arg as { blockchain: BlockchainType }

    if (isObject(objectCurrency)) {
      return UTXO_BLOCKCHAINS.includes(
        objectCurrency.blockchain as BlockchainTypeUTXO
      )
    }

    return UTXO_BLOCKCHAINS.includes(arg as BlockchainTypeUTXO)
  }

  return {
    isUTXO,
    isEVM,
    isSupportedBlockchain,
    isUnsupportedBlockchain,
    isBlockchainEVM,
    isBlockchainUTXO,
  }
}

// Export default type guards for backward compatibility
export const {
  isUTXO,
  isEVM,
  isSupportedBlockchain,
  isUnsupportedBlockchain,
  isBlockchainEVM,
  isBlockchainUTXO,
} = createCryptoTypeGuards(defaultConfig)

// ========== UNIFIED CRYPTO TYPES GENERATOR ==========

export type CryptoTypes<
  T extends ReturnType<typeof createCryptoConfiguration>['types'],
> = T

export type CryptoTypesResult = CryptoTypes<typeof defaultConfig.types>

// Export default types for backward compatibility
export type CoinData = CryptoTypesResult['CoinData']
export type Coin = CryptoTypesResult['Coin']
export type CurrencySymbolsType = CryptoTypesResult['CurrencySymbolsType']
export type CodeByCurrency = CryptoTypesResult['CodeByCurrency']
export type CurrencyByCode = CryptoTypesResult['CurrencyByCode']
export type CurrencySymbols = CryptoTypesResult['CurrencySymbols']
export type CoinNames = CryptoTypesResult['CoinNames']
export type CurrencyUnitExponent = CryptoTypesResult['CurrencyUnitExponent']
export type CoinCodeByIndex = CryptoTypesResult['CoinCodeByIndex']
export type CoinCodes = CryptoTypesResult['CoinCodes']
export type CoinCodesType = CryptoTypesResult['CoinCodesType']
export type CoinType = CryptoTypesResult['CoinType']
export type BlockchainType = CryptoTypesResult['BlockchainType']
export type CoinTypeUTXO = CryptoTypesResult['CoinTypeUTXO']
export type BlockchainTypeUTXO = CryptoTypesResult['BlockchainTypeUTXO']
export type CoinTypeEVM = CryptoTypesResult['CoinTypeEVM']
export type BlockchainTypeEVM = CryptoTypesResult['BlockchainTypeEVM']
export type AllCoinType = CryptoTypesResult['AllCoinType']
export type SupportedCoin<T extends AllCoinType> = Extract<T, CoinType>
export type UnsupportedCoin<T extends AllCoinType> = Exclude<T, CoinType>

// Helper type to infer configuration type
export type CryptoConfigurationResult = ReturnType<
  typeof createCryptoConfiguration
>
